/**
 * useRealtimeEvents — single app-wide WebSocket subscription that forwards
 * server-side TRACK_NEW / TRACK_UPDATE / ALERT_NEW / SENSOR_STATUS events
 * into TanStack Query's cache, keeping the dashboard timeline, live-track
 * list, alerts dropdown, and sensor banner fresh without each component
 * having to poll aggressively.
 *
 * Mount once (MainLayout) so every authenticated page benefits. The hook
 * is a no-op until the auth store reports a tenantId.
 *
 * Flow:
 *   1. Open a WS connection (VITE_WS_URL env, fallback derived from window).
 *   2. Send `{ type: 'subscribe', tenantId }` on connect.
 *   3. On any tenant-scoped message, enqueue a query-cache invalidation
 *      for the matching key. Invalidations are coalesced over a short
 *      window so a burst of TRACK_UPDATEs doesn't trigger a burst of
 *      refetches.
 *   4. Reconnect with exponential backoff on drop.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';

// Tune: wait this long before firing the next invalidation for a given
// query key. 1 s keeps the timeline lively without thrashing refetches
// during a burst.
const COALESCE_MS = 1000;

function resolveWsUrl(): string {
  const envUrl = (import.meta.env.VITE_WS_URL as string | undefined)?.trim();
  if (envUrl) return envUrl;
  // Fall back to same-host /ws, picking ws/wss based on current scheme.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

export function useRealtimeEvents() {
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const queryClient = useQueryClient();

  // Keep the socket + timers in refs so the effect's cleanup closes over
  // the actual live instances instead of stale copies.
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffMs = useRef<number>(1000);
  const pendingInvalidations = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (!tenantId) return;

    let cancelled = false;

    const scheduleInvalidate = (key: readonly unknown[]) => {
      // Coalesce repeated invalidations for the same queryKey so a burst
      // of TRACK_UPDATEs triggers exactly one refetch per COALESCE_MS.
      const sig = JSON.stringify(key);
      if (pendingInvalidations.current.has(sig)) return;
      const t = setTimeout(() => {
        pendingInvalidations.current.delete(sig);
        queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }, COALESCE_MS);
      pendingInvalidations.current.set(sig, t);
    };

    const handleMessage = (raw: MessageEvent) => {
      let msg: { type?: string; payload?: unknown };
      try {
        msg = JSON.parse(typeof raw.data === 'string' ? raw.data : '');
      } catch {
        return;
      }
      if (!msg || !msg.type) return;

      switch (msg.type) {
        case 'TRACK_NEW':
        case 'TRACK_UPDATE':
        case 'TRACK_LOST':
          // Timeline + live track list + map-store live feed.
          scheduleInvalidate(['liveTracks']);
          scheduleInvalidate(['trackStats']);
          break;
        case 'ALERT_NEW':
        case 'ALERT_CLEARED':
          scheduleInvalidate(['alerts']);
          break;
        case 'SENSOR_STATUS':
          // Both the banner (`['sensors']`) and the map layer
          // (`['sensors', 'map']`) pull from the sensors list.
          scheduleInvalidate(['sensors']);
          scheduleInvalidate(['sensors', 'map']);
          break;
        case 'MISSION_UPDATE':
          scheduleInvalidate(['missions']);
          break;
        // Ignore HEARTBEAT / subscribed / pong — connection bookkeeping.
      }
    };

    const connect = () => {
      if (cancelled) return;
      const url = resolveWsUrl();
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        // Retry with backoff — some browsers throw on immediate ws:// in https pages.
        console.warn('[realtime] WebSocket ctor failed, will retry:', err);
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        // Subscribe once the socket is open — the server keyes its tenant
        // broadcast table off this message.
        ws.send(JSON.stringify({ type: 'subscribe', tenantId }));
        backoffMs.current = 1000; // reset backoff on a clean open
      };

      ws.onmessage = handleMessage;

      ws.onerror = () => {
        // Errors are followed by `close` which handles reconnect — avoid
        // double-scheduling here.
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!cancelled) scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const delay = backoffMs.current;
      // Double until ~30 s, then cap.
      backoffMs.current = Math.min(backoffMs.current * 2, 30_000);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      for (const t of pendingInvalidations.current.values()) clearTimeout(t);
      pendingInvalidations.current.clear();
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'unsubscribe' }));
        } catch {
          // socket may already be closing — ignore
        }
      }
      if (ws) ws.close();
      wsRef.current = null;
    };
  }, [tenantId, queryClient]);
}
